import { useNavigate } from 'react-router-dom'

import {
  F0Dialog,
  F0Form,
  f0FormField,
  useF0Form,
  useF0FormDefinition,
} from '@factorialco/f0-react'
import {
  CreateProcurementTypeDocument,
  NewRequestPageDocument,
  ProcurementSettingsPageDocument,
  ProcurementTypesDataCollectionDocument,
} from 'generated/resources/graphql'
import { z } from 'zod'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface Props {
  onClose: () => void
  hasFormsEnabled: boolean
}

const CreateRequestTypeModal = ({ onClose, hasFormsEnabled }: Props) => {
  const { company, employee } = useCurrentSession()
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const [createProcurementType] = useMutation(CreateProcurementTypeDocument, {
    extractorFn: (data) => data.procurementMutations.createProcurementType,
  })

  const { formRef, submit, isSubmitting, hasErrors } = useF0Form()

  const schema = z.object({
    name: f0FormField(z.string().trim().min(1), {
      label: i18n.t('procurement.purchase_requests.settings.types.modals.detail.labels.name'),
    }),
    description: f0FormField(z.string().trim().max(75), {
      label: i18n.t(
        'procurement.purchase_requests.settings.types.modals.detail.labels.description'
      ),
      helpText: i18n.t(
        'procurement.purchase_requests.settings.types.modals.detail.descriptions.length'
      ),
    }),
  })

  const formDefinition = useF0FormDefinition({
    name: 'create-request-type',
    schema,
    defaultValues: { name: '', description: '' },
    onSubmit: async ({ data }) => {
      const result = await createProcurementType({
        variables: {
          authorId: employee.id,
          companyId: company.id,
          description: data.description,
          name: data.name,
        },
        refetchQueries: [
          ProcurementSettingsPageDocument,
          NewRequestPageDocument,
          ProcurementTypesDataCollectionDocument,
        ],
      })

      let fieldErrors: Record<string, string> = {}
      const handled = handleResult(result, (errors) => {
        fieldErrors = errors
      })

      if (!handled.success) {
        return { success: false, errors: fieldErrors }
      }

      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })

      onClose()

      const typeId = handled.data?.type?.id
      if (typeId && hasFormsEnabled) {
        const searchParams = new URLSearchParams({ tab: 'form' })
        navigate({
          pathname: navigation.settings.procurement.requestType.id.fullPath({ id: typeId }),
          search: searchParams.toString(),
        })
      }

      return { success: true }
    },
    submitConfig: { type: 'default', hideSubmitButton: true },
  })

  return (
    <F0Dialog
      isOpen
      onClose={onClose}
      title={i18n.t('procurement.purchase_requests.settings.types.modals.detail.title')}
      description={i18n.t('procurement.purchase_requests.settings.types.modals.detail.description')}
      primaryAction={{
        label: i18n.t('procurement.purchase_requests.settings.types.modals.detail.buttons.save'),
        onClick: submit,
        loading: isSubmitting,
        disabled: hasErrors,
      }}
      secondaryAction={{
        label: i18n.t('procurement.purchase_requests.settings.types.modals.detail.buttons.cancel'),
        onClick: onClose,
      }}
    >
      <F0Form formRef={formRef} formDefinition={formDefinition} />
    </F0Dialog>
  )
}

export default CreateRequestTypeModal
